import { useAuth } from '../../contexts/AuthContext';
import { watchNotificationCounts } from '../../services/watchNotificationCounts';
import { desktopRoute } from '../DesktopLayout/desktopRoutes';
import React, { useState, useContext, useEffect, useRef } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  LayoutGrid,
  UserStar,
  Plus,
  MoreHorizontal,
  FileText,
  BarChart3,
  Video,
  Newspaper,
  ClipboardList,
  Archive,
  SlidersHorizontal
} from 'lucide-react';
import './BottomNavbar.css';
import { ModalContext } from '../ModalContext';
import { useTheme } from '../../contexts/ThemeContext';
import useMotionPresence from '../Motion/useMotionPresence';
import CreateMenuPreferences from './CreateMenuPreferences';
import { readCreateMenuSettings } from './createMenuSettings';

// Reveal shortcuts in their saved reading order.
const FAN_DELAYS = [0, 45, 90, 135, 180, 225];
export const CREATE_MENU_HOLD_MS = 1000;

const bottomNavLoaders = [
  () => import('../../pages/TalentBadges/TalentsBadges'),
  () => import('../../pages/IslamicHubPage/IslamicHub'),
  () => import('../../pages/ProfileMenu/ProfileMenu'),
];

const BottomNavbar = () => {
  const { currentUserId } = useAuth();
  const [unread, setUnread] = useState({ account: null, total: 0 });
  useEffect(() => watchNotificationCounts(currentUserId, counts => setUnread({ account: currentUserId, total: Number(counts?.total) || 0 })), [currentUserId]);
  const hasUnread = unread.account === currentUserId && unread.total > 0;
  const location = useLocation();
  const hasDesktopNavigation = Boolean(desktopRoute(location.pathname)) || location.pathname.startsWith('/hashtag/') || location.pathname.startsWith('/list/') || ['/', '/post', '/more', '/messages', '/talents-badges', '/islamic', '/saved-posts', '/notifications', '/drafts', '/quran', '/create', '/create-poll', '/create-quiz', '/store', '/frames-store', '/sticker-store', '/reaction-store', '/profile-theme-store', '/bubble-store', '/bubble-store/manage'].includes(location.pathname);
  const navigate = useNavigate();
  const [showCreateMenu, setShowCreateMenu] = useState(false);
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const [preferences, setPreferences] = useState(() => ({ account: currentUserId, ...readCreateMenuSettings(currentUserId) }));
  const [holding, setHolding] = useState(false);
  const press = useRef({ timer: null, active: false, suppressClick: false });
  const { present: menuPresent, exiting: menuExiting } = useMotionPresence(showCreateMenu);
  const { currentModal } = useContext(ModalContext);

  const { theme } = useTheme();
  const { resolvedBottomNavBarDirection, t, language } = useLanguage();
  const ar = language === 'ar';
  const hiddenOptions = preferences.account === currentUserId ? preferences.hidden : [];
  const preferredOrder = preferences.account === currentUserId ? preferences.order : readCreateMenuSettings(currentUserId).order;
  const cancelHold = () => { clearTimeout(press.current.timer); press.current.active = false; setHolding(false); };
  const openPreferences = () => { cancelHold(); setShowCreateMenu(false); setPreferencesOpen(true); };
  useEffect(() => {
    cancelHold(); setPreferencesOpen(false); setShowCreateMenu(false);
    setPreferences({ account: currentUserId, ...readCreateMenuSettings(currentUserId) });
  }, [currentUserId]);
  useEffect(() => {
    const cancel = () => { if (press.current.active) press.current.suppressClick = true; cancelHold(); };
    window.addEventListener('blur', cancel);
    document.addEventListener('visibilitychange', cancel);
    return () => { clearTimeout(press.current.timer); window.removeEventListener('blur', cancel); document.removeEventListener('visibilitychange', cancel); };
  }, []);
  useEffect(() => { cancelHold(); setShowCreateMenu(false); setPreferencesOpen(false); }, [location.pathname]);
  const startHold = event => {
    if (event.button !== 0 || event.isPrimary === false || preferencesOpen) return;
    cancelHold();
    press.current = { active: true, suppressClick: false, pointerId: event.pointerId, x: event.clientX, y: event.clientY };
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setHolding(true);
    press.current.timer = setTimeout(() => {
      if (!press.current.active) return;
      press.current.suppressClick = true;
      openPreferences();
    }, CREATE_MENU_HOLD_MS);
  };

  useEffect(() => {
    const media = window.matchMedia?.('(min-width: 1024px)');
    if (!hasDesktopNavigation || !media) return undefined;
    const closeMobileMenu = () => { if (media.matches) { cancelHold(); setShowCreateMenu(false); setPreferencesOpen(false); } };
    closeMobileMenu();
    media.addEventListener?.('change', closeMobileMenu);
    return () => media.removeEventListener?.('change', closeMobileMenu);
  }, [hasDesktopNavigation]);

  useEffect(() => {
    if (window.navigator.connection?.saveData) return undefined;
    const schedule = window.requestIdleCallback || ((callback) => window.setTimeout(callback, 800));
    const cancel = window.cancelIdleCallback || window.clearTimeout;
    let cancelled = false;
    let handle;
    let index = 0;
    // Warm only primary destinations, one idle slot at a time. Loading nine
    // screens together competed with the feed's first render and media work.
    const next = () => {
      if (cancelled || index === bottomNavLoaders.length) return;
      handle = schedule(() => {
        if (!cancelled) bottomNavLoaders[index++]().catch(() => {}).then(next);
      });
    };
    next();
    return () => { cancelled = true; cancel(handle); };
  }, []);

  const createOptions = [
    { path: '/create?type=video', label: t('bottom_nav_create_video'), Icon: Video, color: '#F43F5E', tint: 'rgba(244, 63, 94, 0.14)' },
    { path: '/create-poll', label: t('bottom_nav_create_poll'), Icon: BarChart3, color: '#10B981', tint: 'rgba(16, 185, 129, 0.14)' },
    { path: '/create', label: t('bottom_nav_create_post'), Icon: FileText, color: '#3B82F6', tint: 'rgba(59, 130, 246, 0.14)' },
    { path: '/create?type=newspaper', label: t('bottom_nav_create_newspaper'), Icon: Newspaper, color: '#F59E0B', tint: 'rgba(245, 158, 11, 0.14)' },
    { path: '/create-quiz', label: t('bottom_nav_create_quiz'), Icon: ClipboardList, color: '#8B5CF6', tint: 'rgba(139, 92, 246, 0.14)' },
    { path: '/drafts', label: t('bottom_nav_drafts'), Icon: Archive, color: '#64748B', tint: 'rgba(100, 116, 139, 0.14)' },
  ];

  const renderFanItem = ({ path, label, Icon, color, tint }, index) => (
    <button type="button"
      key={path}
      className="bnav-fan-item"
      style={{ '--delay': `${FAN_DELAYS[index]}ms` }}
      onClick={() => handleMenuItemClick(path)}
    >
      <div className="bnav-fan-bubble" style={{ backgroundColor: tint }}>
        <Icon size={22} color={color} />
      </div>
      <span className="bnav-fan-label">{label}</span>
    </button>
  );

  const handleCreateClick = (e) => {
    e.preventDefault();
    if (press.current.suppressClick) { press.current.suppressClick = false; return; }
    setShowCreateMenu(!showCreateMenu);
  };

  const handleMenuItemClick = (path) => {
    setShowCreateMenu(false);
    navigate(path, { replace: true });
  };
  const orderedOptions = preferredOrder.map(path=>createOptions.find(option=>option.path===path)).filter(Boolean);
  const visibleOptions = orderedOptions.filter(option => !hiddenOptions.includes(option.path));

  // hide bottom navbar when full-screen overlays are open
  if (currentModal === 'single-video' || currentModal === 'user-status') return null;

  return (
    <>
      {/* Overlay للخلفية */}
      {menuPresent && (
        <div
          className={`bnav-overlay ${hasDesktopNavigation ? 'bnav-desktop-overlay' : ''} ${menuExiting ? 'bnav-exiting' : ''}`}
          onClick={() => setShowCreateMenu(false)}
        />
      )}

      <div className={`bottom-navbar ${hasDesktopNavigation ? 'bottom-navbar--desktop' : ''} ${theme === 'dark' ? 'theme-dark-space' : 'theme-light-space'} ${resolvedBottomNavBarDirection}`} dir={resolvedBottomNavBarDirection}>
        {/* Home */}
        <NavLink
          to="/"
          replace
          className={() => {
            const isHomeActive = location.pathname === '/' || location.pathname === '/post';
            return `bnav-item ${isHomeActive ? 'active' : ''} ${theme === 'dark' ? 'theme-dark-item' : 'theme-light-item'}`;
          }}
        >
          <span className="bnav-icon-wrap">
            <LayoutGrid size={22} />
          </span>
          <span className="bnav-label">{t('bottom_nav_home')}</span>
        </NavLink>

        <NavLink
          to="/talents-badges"
          replace
          className={({ isActive }) => `bnav-item ${isActive ? 'active' : ''} ${theme === 'dark' ? 'theme-dark-item' : 'theme-light-item'}`}
        >
          <span className="bnav-icon-wrap">
            <UserStar size={22} />
          </span>
          <span className="bnav-label">{t('bottom_nav_missions')}</span>
        </NavLink>

        {/* Center button with fan menu */}
        <div className="bnav-item bnav-center-container">
          <button
            className={`bnav-center-btn ${showCreateMenu ? 'menu-open' : ''}`}
            style={{ '--bnav-hold-duration': `${CREATE_MENU_HOLD_MS}ms` }}
            onClick={handleCreateClick}
            onPointerDown={startHold}
            onPointerMove={event => { if (press.current.active && Math.hypot(event.clientX - press.current.x, event.clientY - press.current.y) > 12) { press.current.suppressClick = true; cancelHold(); } }}
            onPointerUp={cancelHold}
            onPointerCancel={() => { press.current.suppressClick = true; cancelHold(); }}
            onLostPointerCapture={cancelHold}
            onContextMenu={event => event.preventDefault()}
            onKeyDown={event => { if (event.key === 'ContextMenu' || (event.shiftKey && event.key === 'F10')) { event.preventDefault(); openPreferences(); } }}
            title={ar ? 'اضغط للإنشاء، أو اضغط مطولاً ثانية واحدة للتخصيص' : 'Click to create; hold for 1 second to customize'}
            aria-label={t('bottom_nav_create_post')}
            aria-expanded={showCreateMenu}
          >
            <Plus size={26} color="#fff" />
            {holding && <svg className="bnav-hold-ring" viewBox="0 0 64 64" aria-hidden="true"><rect x="3" y="3" width="58" height="58" rx="21" pathLength="100"/></svg>}
          </button>

          {menuPresent && (
            <div className={`bnav-fan-menu ${menuExiting ? 'bnav-exiting' : ''} ${theme === 'dark' ? 'theme-panel-dark' : 'theme-panel-light'}`} inert={menuExiting || undefined} dir={resolvedBottomNavBarDirection}>
              <div className="bnav-fan-row bnav-fan-row-top">
                {visibleOptions.slice(0, 3).map((opt, i) => renderFanItem(opt, i))}
              </div>
              {visibleOptions.length > 3 && <div className="bnav-fan-row bnav-fan-row-bottom">
                {visibleOptions.slice(3).map((opt, i) => renderFanItem(opt, 3 + i))}
              </div>}
              {!visibleOptions.length && <p className="bnav-fan-empty">{ar ? 'اختر اختصاراتك المفضلة من التخصيص' : 'Choose your favorite shortcuts below'}</p>}
              <button type="button" className="bnav-customize" onClick={openPreferences}><SlidersHorizontal size={15}/>{ar ? 'تخصيص القائمة' : 'Customize menu'}</button>
            </div>
          )}
        </div>

        {/* Team */}
        <NavLink
          to="/islamic"
          replace
          className={({ isActive }) =>
            `bnav-item ${isActive ? "active" : ""} ${
              theme === "dark" ? "theme-dark-item" : "theme-light-item"
            }`
          }
        >
          {({ isActive }) => (
            <>
              <span className="bnav-icon-wrap">
                <svg
                  className={`bnav-team-icon ${isActive ? "active" : ""}`}
                  viewBox="0 0 350 270"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fill="currentColor"
                    d="m183.88 15.84c4.46 3.04 8.24 6.61 9.71 11.92 0.49 2.66 0.36 5.4 0.35 8.11q0 1.06 0.01 2.12 0 2.91 0 5.83 0 3.14 0.01 6.29 0 5.45 0 10.9 0.01 7.88 0.02 15.76 0.03 12.81 0.04 25.61 0 0.76 0 1.55 0.01 8.55 0.02 17.09 0 3.1 0 6.2c0 0.76 0 0.76 0 1.54q0.01 12.76 0.04 25.51 0.01 7.87 0.01 15.74 0 5.39 0.01 10.77 0.01 3.11 0.01 6.22-0.01 2.85 0.01 5.7 0 1.51-0.01 3.02c0.04 4.67 0.76 8.37 3.24 12.37q0.37 0.62 0.74 1.24c2.8 3.97 6.88 5.83 11.53 6.77 4.83 0.51 9.16-0.24 13.1-3.16 4.18-3.54 7.37-7.13 7.92-12.81 0.2-3.56 0.16-7.13 0.14-10.7q0-1.81 0.01-3.63 0.01-3.88 0-7.77-0.01-5.62 0.01-11.25 0.02-9.13 0.01-18.26 0-0.55 0-1.11 0-2.21 0-4.42 0-6.1 0.01-12.19 0-0.55 0-1.1 0-9.11 0.02-18.22 0.01-6.11 0-12.22 0-3.8 0.01-7.59 0-1.76-0.01-3.52 0-2.41 0.01-4.81-0.01-0.7-0.01-1.41c0.02-2.49 0.19-4.02 1.73-6.07 0.35-1.12 0.35-1.12 0.51-2.04q0.5 0 1.02 0c0.17-0.55 0.17-0.55 0.35-1.12 1.49-3.2 4.68-5.5 7.8-7.04 1.71-0.37 3.36-0.51 5.1-0.51q0-0.51 0-1.02c3.91-0.18 7.11-0.03 10.71 1.53 0.51 0.19 0.51 0.19 1.03 0.39 4.22 1.81 7.81 5.51 9.57 9.74 1.11 3.04 1.35 5.73 1.32 8.94 0 0.78 0 0.78 0.01 1.57q0 2.57-0.01 5.15 0 1.85 0 3.7 0 4.5-0.01 9-0.01 5.25-0.01 10.49 0 9.36-0.02 18.72-0.02 9.08-0.02 18.15 0 0.56 0 1.14 0 1.12 0 2.25 0 0.55 0 1.12c0 0.55 0 0.55 0 1.12q-0.01 8.66-0.02 17.32-0.01 3.14-0.01 6.27 0 5.44-0.01 10.88-0.01 1.8-0.01 3.61 0 2.46-0.01 4.92c0 0.72 0 0.72 0 1.44-0.03 7.34-0.03 7.34-1.09 8.4q-0.11 1.03-0.19 2.07c-0.25 2.41-0.92 4.37-1.85 6.6-0.21 0.58-0.21 0.58-0.43 1.17-1.99 5.36-5.01 9.94-8.39 14.51q-0.41 0.54-0.82 1.1-0.38 0.51-0.78 1.02c-0.34 0.46-0.34 0.46-0.68 0.92q-0.31 0.32-0.63 0.65-0.5 0-1.02 0-0.5 1.01-1.02 2.04-0.5 0-1.02 0c-0.15 0.45-0.15 0.45-0.31 0.9-0.96 1.56-2.23 2.2-3.77 3.18q-0.46 0.49-0.95 0.99c-1.18 1.15-2.31 1.82-3.76 2.58q-0.68 0.37-1.38 0.74-0.76 0.39-1.55 0.79-0.47 0.25-0.94 0.5c-2.45 1.26-4.9 2.27-7.51 3.13q-0.59 0.2-1.19 0.41c-3.27 1.05-6.46 1.58-9.87 1.92-1.36 0.01-1.36 0.01-1.91 0.67q-1.3 0.07-2.6 0.07-0.78 0-1.59 0-0.83 0-1.68-0.01-0.82 0.01-1.68 0.01-0.78 0-1.59 0c-0.72 0-0.72 0-1.47 0-1.12-0.07-1.12-0.07-1.63-0.58q-1.01-0.17-2.03-0.27c-5.64-0.69-10.19-1.79-15.31-4.32q-0.89-0.37-1.8-0.76c-1.6-0.7-3.14-1.44-4.67-2.27-0.7-0.36-0.7-0.36-1.41-0.74-1.32-0.83-2.27-1.74-3.34-2.86q-0.5-0.25-1.02-0.51 0-0.5 0-1.02c-1.43-0.44-1.43-0.44-3.06-0.51-1.14 0.93-1.14 0.93-2.04 2.04q-0.75 0.25-1.53 0.51-0.77 0.76-1.53 1.53-1.25 0.85-2.55 1.63-0.66 0.4-1.34 0.82c-1.21 0.61-1.21 0.61-2.74 0.61q0 0.51 0 1.02-1.25 0-2.55 0 0 0.51 0 1.02-0.47 0.14-0.95 0.28-0.65 0.2-1.31 0.39-0.71 0.21-1.43 0.42-1.76 0.56-3.48 1.21c-5.46 1.81-15.96 4.5-21.39 1.78q-1.04-0.17-2.09-0.29c-5.28-0.68-9.86-1.58-14.74-3.79q-0.85-0.35-1.72-0.71c-1.96-0.87-3.79-1.87-5.64-2.95q-0.48-0.28-0.98-0.57-1.19-0.69-2.36-1.38 0-0.5 0-1.02-0.54-0.17-1.09-0.35c-1.84-0.84-3.04-1.83-4.52-3.22q0-0.5 0-1.02-1.01-0.5-2.04-1.02 0-0.5 0-1.02-0.44-0.17-0.9-0.35c-1.43-0.83-2.17-1.81-3.18-3.12q-0.5-0.65-1.02-1.31c-0.51-0.66-0.51-0.66-1.02-1.34q-0.53-0.64-1.08-1.31c-0.85-1.09-1.5-2.1-2.11-3.34q-0.26-0.5-0.52-1.02c-0.37-0.96-0.37-0.96-0.37-2.48q-0.5 0-1.02 0c-4.22-9.92-5.69-18.95-5.67-29.69q0-1.32 0-2.63 0-3.54 0.01-7.08 0-3.71 0-7.41 0-7.02 0.01-14.03 0.01-7.98 0.01-15.97 0.01-16.42 0.03-32.84-0.43 0.34-0.86 0.67-0.55 0.42-1.12 0.86-0.55 0.43-1.11 0.87c-0.99 0.66-0.99 0.66-2.01 0.66q-0.5 1.01-1.02 2.04-0.5 0-1.02 0-0.25 0.76-0.51 1.53c-1.25 1.37-1.25 1.37-2.55 2.55q-0.5 0-1.02 0-0.5 1.01-1.02 2.04-0.5 0-1.02 0-0.06 0.43-0.13 0.86c-0.49 1.53-1.41 2.49-2.42 3.73q-0.25 0.76-0.51 1.53-0.5 0-1.02 0-0.17 0.58-0.35 1.18c-0.56 1.59-1.2 3.04-1.92 4.56-3.27 7.16-3.98 14.36-4.1 22.15-0.16 6.39-2.38 11.2-7.02 15.62-4.05 3.34-7.52 4.09-12.68 4.14q-0.65 0.01-1.31 0.02c-4.12-0.06-7.23-1.39-10.87-3.3q0-0.51 0-1.02-0.45-0.19-0.91-0.39c-3.13-1.74-5.12-4.97-6.23-8.28-0.61-3.11-0.6-6.15-0.6-9.31q0-0.87 0-1.77c0.09-13.39 0.09-13.39 1.62-14.93q0.12-1.16 0.2-2.32c0.19-2.17 0.63-3.92 1.46-5.91 0.44-1.05 0.44-1.05 0.83-2.61 1.28-4.26 3.2-8.28 5.16-12.26q0.25-0.5 0.5-1.02c0.85-1.72 1.78-3.29 2.91-4.84 1.23-1.53 1.23-1.53 1.69-3.17q0.51 0 1.02 0c0.19-0.54 0.19-0.54 0.37-1.1 0.74-1.65 1.68-2.81 2.85-4.19 1.37-1.63 2.69-3.24 3.89-5 0.52-0.71 0.52-0.71 1.05-1.44q0.51 0 1.02 0 0.51-1 1.02-2.03 0.51 0 1.02 0 0.16-0.43 0.32-0.87c0.77-1.29 1.69-2.08 2.88-2.99 0.96-0.78 0.96-0.78 2.06-1.93 1.42-1.41 2.89-2.69 4.43-3.97q0.77-0.64 1.56-1.31c1.32-1.03 2.61-1.88 4.05-2.7q1.56-1.02 3.13-2.05c0.95-0.5 0.95-0.5 2.48-0.5q0-0.51 0-1.02 0.77-0.39 1.57-0.79 0.5-0.25 1.02-0.51 1.12-0.56 2.24-1.12 2.5-1.23 4.97-2.53c3.36-1.76 6.71-3.01 10.32-4.14 1.83-0.58 1.83-0.58 3.63-1.4 1.62-0.68 3.02-1.04 4.75-1.31 1.71-0.14 1.71-0.14 3.12-0.95 6.74-0.48 11.87 0.78 17.15 4.97 4.23 3.82 6.19 9.26 6.49 14.88 0.1 3.08 0.08 6.15 0.07 9.22q0 1.77 0.01 3.54 0 3.8 0 7.59-0.01 6 0 12 0.03 8.42 0.02 16.83c0 0.54 0 0.54 0 1.08q0 2.15 0 4.32 0 5.94 0.01 11.89 0 0.53 0 1.08 0 8.9 0.02 17.79 0.01 5.97 0 11.94 0 3.7 0.01 7.41 0 1.72-0.01 3.44 0 2.34 0.01 4.69-0.01 0.68-0.01 1.37c0.03 3.77 0.95 6.8 3.1 9.96 1.17 1.74 1.17 1.74 1.17 2.87q1.01 0.5 2.04 1.02 0 0.5 0 1.02 0.46 0.09 0.93 0.18c1.11 0.33 1.11 0.33 2.64 1.35q1.27 0.29 2.55 0.51 0 0.5 0 1.02c3.54 0 7.07 0 10.71 0q0-0.51 0-1.02c0.67-0.19 0.67-0.19 1.34-0.39 1.82-0.67 3.19-1.57 4.78-2.67q0.57-0.25 1.15-0.51c1.17-0.67 1.41-1.32 1.91-2.55q0.51-1.02 1.02-2.04 0.51 0 1.02 0 0.09-0.51 0.2-1.02c0.31-1.53 0.31-1.53 0.83-3.24 0.77-3.06 0.64-6.15 0.62-9.28q0.01-1.06 0.01-2.11 0-2.88-0.01-5.76 0-3.11 0.01-6.22 0-5.39-0.01-10.77 0-8.53 0.01-17.05 0.01-2.92 0.01-5.84 0-0.72 0-1.47 0.01-8.29 0.01-16.59 0-0.75 0.01-1.53 0.01-12.28-0.01-24.56 0-12.62 0.03-25.24 0.01-7.78 0-15.55-0.01-5.33 0.01-10.67 0.01-3.07-0.01-6.14-0.01-2.82 0.01-5.64 0-1.49-0.01-2.99c0.04-4.42 1.05-7.91 2.88-11.92q0.51 0 1.02 0 0.21-0.61 0.41-1.24 0.31-0.65 0.61-1.31c0.76-0.25 0.76-0.25 1.53-0.51q0.24-0.5 0.48-1.02c0.54-1.02 0.54-1.02 1.63-1.4q0.47-0.06 0.95-0.13 0-0.5 0-1.02 0.95-0.5 1.91-0.99 0.53-0.27 1.08-0.55c4.81-2.19 12.08-1.81 16.66 0.68z"
                  />
                  <path
                    fill="currentColor"
                    d="m319.77 65.15c0.47 0.15 0.47 0.15 0.95 0.3 4.86 1.59 7.54 4.46 10.39 8.63 2.28 4.52 2.65 8.99 2.63 13.98q0 0.96 0 1.94 0.01 2.65 0.01 5.3-0.01 2.86 0 5.73 0.02 5.6 0.01 11.21 0 4.55 0 9.11 0 0.65 0 1.31 0 1.32 0.01 2.64 0 12.37-0.01 24.74 0 11.3 0.02 22.6 0.02 11.62 0.01 23.24 0 6.52 0.01 13.04 0.01 5.54 0 11.09-0.01 2.83 0 5.66 0.01 3.07-0.01 6.14c0.01 0.88 0.01 0.88 0.02 1.78-0.06 6.21-1.61 12.02-5.88 16.67-4.55 4.1-9.92 5.97-16 5.75-1.61-0.16-2.92-0.52-4.4-1.16q-1.04-0.36-2.07-0.73c-3.43-1.32-5.36-3.06-7.62-5.9-0.47-0.55-0.47-0.55-0.96-1.11-0.57-0.93-0.57-0.93-0.57-2.97q-0.5 0-1.02 0c-0.9-4.1-1.17-8.01-1.15-12.21q0-0.96 0-1.95-0.01-2.65 0-5.3 0-2.87-0.01-5.74 0-5.61 0-11.22 0.01-4.57 0-9.13c0-0.65 0-0.65 0-1.31q0-1.33 0-2.65 0-12.38 0.01-24.76 0-10.61 0-21.22-0.01-12.34-0.01-24.67 0-1.32 0-2.64 0-0.64 0-1.31 0.01-4.55 0-9.11 0-5.55 0-11.11 0.01-2.83 0-5.66 0-3.07 0.01-6.15 0-0.88-0.01-1.79c0.02-3.4 0.34-6.54 1.16-9.84q0.51 0 1.02 0 0.03-0.51 0.06-1.02c1.26-4.31 4.82-7.07 8.61-9.18 4.79-2.17 9.81-3.12 14.79-1.02z"
                  />
                  <path
                    fill="currentColor"
                    d="m332.01 81.47q0.75 0 1.53 0c0 51.83 0 103.66 0 157.06q-0.76 0-1.53 0c0-51.83 0-103.66 0-157.06z"
                  />
                  <path
                    fill="currentColor"
                    d="m295.29 176.32q0.25 0 0.51 0c0 20.87 0 41.73 0 63.23q-0.75-0.5-1.53-1.02c-0.19-0.97-0.19-0.97-0.19-2.18q0-0.69-0.01-1.4 0.01-0.75 0.01-1.53 0-0.79 0-1.61-0.01-2.65 0.01-5.3 0-1.85 0-3.69 0-3.87 0.01-7.75 0.01-4.94 0.01-9.89 0-3.81 0-7.63 0.01-1.82 0.01-3.64c-0.01-12.19-0.01-12.19 1.17-17.59z"
                  />
                  <path
                    fill="currentColor"
                    d="m295.8 80.96c0 26.75 0 53.51 0 81.08q-0.5 0.25-1.02 0.51-0.15-1.9-0.28-3.79-0.05-0.52-0.09-1.07c-0.13-1.84-0.12-3.47 0.11-5.31 0.36-3.09 0.33-6.16 0.33-9.26q0-0.98 0-1.99 0-2.68 0-5.35 0-2.81 0-5.62 0-4.7 0-9.41-0.01-5.44 0-10.88 0-4.69 0-9.37 0-2.79 0-5.58 0-3.11 0-6.21 0-0.93 0-1.87 0-0.85 0-1.71 0-0.73 0-1.48c0.01-1.18 0.01-1.18-0.58-2.18 1.02-0.51 1.02-0.51 1.53-0.51z"
                  />
                  <path
                    fill="currentColor"
                    d="m71.93 119.2c-0.58 1.74-1.09 2.07-2.55 3.06q-0.51-0.5-1.02-1.02c1.85-2.04 1.85-2.04 3.57-2.04z"
                  />
                  <path
                    fill="currentColor"
                    d="m242.25 66.17c-0.41 0.99-0.41 0.99-1.01 2.04-1.53 0.51-1.53 0.51-3.06 0 2.93-2.04 2.93-2.04 4.07-2.04z"
                  />
                </svg>
              </span>
              <span className="bnav-label">{t('bottom_nav_team')}</span>
            </>
          )}
        </NavLink>

        {/* More */}
        <NavLink
          to="/more"
          replace
          className={({ isActive }) => `bnav-item ${isActive ? 'active' : ''} ${theme === 'dark' ? 'theme-dark-item' : 'theme-light-item'}`}
        >
          <span className="bnav-icon-wrap">
            <MoreHorizontal size={22} />
            {hasUnread && <span className="bnav-unread-dot" role="status" aria-label={t('bottom_nav_unread_notifications', 'Unread notifications')} />}
          </span>
          <span className="bnav-label">{t('bottom_nav_more')}</span>
        </NavLink>
      </div>
      {preferencesOpen && <CreateMenuPreferences key={currentUserId || 'guest'} options={orderedOptions} hidden={hiddenOptions} accountId={currentUserId} theme={theme} language={language} onSave={value => setPreferences({ account: currentUserId, ...value })} onClose={() => setPreferencesOpen(false)}/>}
    </>
  );
};

export default BottomNavbar;
